// src/pages/customers/ExtendedCustomersPage.tsx
import {
  AlertTriangle,
  Calendar,
  ChevronDown,
  ChevronRight,
  Clock,
  Loader2,
  Phone,
  Router,
  Search,
  TrendingUp,
  User,
  Users,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_CONFIG, fetchApp } from "../../services/apiService";

interface ExtensionLite {
  id: string | number;
  previous_access_expires_at?: string | null;
  new_access_expires_at: string;
  reason?: string | null;
  created_by?: string | null;
  created_at?: string | null;
}

interface ExtendedCustomerRow {
  secret_id: string | number;
  username: string;
  account_number: string | number;
  phone_number: string;
  plan_name?: string | null;
  router_name?: string | null;
  service_expires_at?: string | null;
  access_expires_at?: string | null;
  last_extension_at?: string | null;
  last_extension_new_access_expires_at?: string | null;
  last_extension_reason?: string | null;
  extension_count: number;
  extensions: ExtensionLite[];
}

type StatusFilter = "all" | "active" | "inactive";

const dateFormatter = new Intl.DateTimeFormat("en-KE", {
  day: "2-digit",
  month: "short",
  year: "numeric",
});

const dateTimeFormatter = new Intl.DateTimeFormat("en-KE", {
  day: "2-digit",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

const formatDateTime = (value?: string | null) => {
  if (!value) return "—";

  const d = new Date(value);

  if (Number.isNaN(d.getTime())) {
    return value;
  }

  return dateTimeFormatter.format(d);
};

const formatDate = (value?: string | null) => {
  if (!value) return "—";

  const d = new Date(value);

  if (Number.isNaN(d.getTime())) {
    return value;
  }

  return dateFormatter.format(d);
};

const getInitials = (name: string) =>
  String(name || "")
    .split(" ")
    .filter(Boolean)
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "C";

const AVATAR_PALETTES = [
  "bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200",
  "bg-sky-100 text-sky-700 ring-1 ring-sky-200",
  "bg-violet-100 text-violet-700 ring-1 ring-violet-200",
  "bg-amber-100 text-amber-700 ring-1 ring-amber-200",
  "bg-teal-100 text-teal-700 ring-1 ring-teal-200",
  "bg-rose-100 text-rose-700 ring-1 ring-rose-200",
];

const avatarColor = (name: string) =>
  AVATAR_PALETTES[
    (name || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0) %
      AVATAR_PALETTES.length
  ];

const isExtensionActive = (accessExpiresAt?: string | null) => {
  if (!accessExpiresAt) return false;

  const expiry = new Date(accessExpiresAt);

  return !Number.isNaN(expiry.getTime()) && expiry > new Date();
};

const safeTime = (value?: string | null) => {
  if (!value) return 0;

  const time = new Date(value).getTime();

  return Number.isNaN(time) ? 0 : time;
};

const ExtendedCustomersPage: React.FC = () => {
  const navigate = useNavigate();

  const [rows, setRows] = useState<ExtendedCustomerRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [openRowId, setOpenRowId] = useState<string | number | null>(null);

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await fetchApp(`${API_CONFIG.baseUrl}/secrets/extensions`);

      setRows(Array.isArray(data) ? data : data?.items || data?.data || []);
    } catch (e: any) {
      console.error(e);
      setError(e?.message || "Failed to load extended customers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredRows = useMemo(() => {
    return rows
      .filter((r) => {
        if (search.trim()) {
          const q = search.toLowerCase();

          const matchesSearch =
            r.username?.toLowerCase().includes(q) ||
            String(r.account_number).toLowerCase().includes(q) ||
            r.phone_number?.toLowerCase().includes(q) ||
            (r.plan_name || "").toLowerCase().includes(q) ||
            (r.router_name || "").toLowerCase().includes(q) ||
            (r.last_extension_reason || "").toLowerCase().includes(q);

          if (!matchesSearch) return false;
        }

        if (statusFilter === "all") return true;

        const active = isExtensionActive(r.access_expires_at);

        if (statusFilter === "active") {
          return active;
        }

        return !active;
      })
      .sort((a, b) => {
        const da = safeTime(
          a.last_extension_at || a.last_extension_new_access_expires_at,
        );

        const db = safeTime(
          b.last_extension_at || b.last_extension_new_access_expires_at,
        );

        return db - da;
      });
  }, [rows, search, statusFilter]);

  const stats = useMemo(() => {
    const active = rows.filter((r) => isExtensionActive(r.access_expires_at));
    const totalExtensions = rows.reduce(
      (sum, r) => sum + Number(r.extension_count || 0),
      0,
    );

    return {
      total: rows.length,
      active: active.length,
      totalExtensions,
    };
  }, [rows]);

  return (
    <div className="min-h-screen bg-[#eef3f8] px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-[1600px] space-y-6">
        {/* Header */}
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
          <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />

          <div className="relative overflow-hidden">
            <div className="absolute -right-16 -top-20 h-56 w-56 rounded-full bg-emerald-200/35 blur-2xl" />
            <div className="absolute -bottom-24 left-1/3 h-56 w-56 rounded-full bg-cyan-200/35 blur-2xl" />

            <div className="relative flex flex-col gap-4 p-5 sm:p-6 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[11px] font-black uppercase tracking-[0.08em] text-emerald-700">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  Temporary Access
                </div>

                <h1 className="mt-3 text-[24px] font-black tracking-[-0.5px] text-slate-900">
                  Extended Customers
                </h1>

                <p className="mt-1 max-w-2xl text-[13.5px] font-medium text-slate-500">
                  Monitor customers who have been granted temporary internet
                  access extensions and review their extension history.
                </p>
              </div>

              <div className="rounded-3xl border border-amber-200 bg-amber-50/90 px-4 py-3 shadow-sm shadow-amber-200/40">
                <div className="flex items-start gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-amber-100 text-amber-700">
                    <Clock className="h-4 w-4" />
                  </div>

                  <div>
                    <p className="text-[12.5px] font-black text-amber-900">
                      Access Extension Tracking
                    </p>

                    <p className="text-[11.5px] font-medium text-amber-700">
                      Active status is based on the access expiry date.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 px-5 py-5 shadow-sm shadow-slate-300/40">
            <div className="absolute -right-10 -top-10 h-28 w-28 rounded-full bg-slate-200/60" />

            <div className="relative flex items-center gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-slate-200 text-slate-700">
                <Users className="h-5 w-5" />
              </div>

              <div>
                <p className="text-[11px] font-black uppercase tracking-widest text-slate-500">
                  Total Customers
                </p>

                <p className="mt-0.5 text-3xl font-black tracking-tight text-slate-900">
                  {stats.total}
                </p>

                <p className="text-[11px] font-bold text-slate-500">
                  With access extensions
                </p>
              </div>
            </div>
          </div>

          <div className="relative overflow-hidden rounded-3xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 px-5 py-5 shadow-sm shadow-emerald-200/50">
            <div className="absolute -right-10 -top-10 h-28 w-28 rounded-full bg-emerald-200/50" />

            <div className="relative flex items-center gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
                <TrendingUp className="h-5 w-5" />
              </div>

              <div>
                <p className="text-[11px] font-black uppercase tracking-widest text-emerald-600">
                  Active Extensions
                </p>

                <p className="mt-0.5 text-3xl font-black tracking-tight text-emerald-800">
                  {stats.active}
                </p>

                <p className="text-[11px] font-bold text-emerald-600">
                  Currently valid
                </p>
              </div>
            </div>
          </div>

          <div className="relative overflow-hidden rounded-3xl border border-sky-200 bg-gradient-to-br from-sky-50 to-cyan-50 px-5 py-5 shadow-sm shadow-sky-200/50">
            <div className="absolute -right-10 -top-10 h-28 w-28 rounded-full bg-sky-200/50" />

            <div className="relative flex items-center gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-sky-100 text-sky-700">
                <Clock className="h-5 w-5" />
              </div>

              <div>
                <p className="text-[11px] font-black uppercase tracking-widest text-sky-600">
                  Total Extensions
                </p>

                <p className="mt-0.5 text-3xl font-black tracking-tight text-sky-800">
                  {stats.totalExtensions}
                </p>

                <p className="text-[11px] font-bold text-sky-600">
                  All-time extension count
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex flex-col gap-3 rounded-3xl border border-slate-200 bg-slate-50/95 px-4 py-4 shadow-lg shadow-slate-300/25 sm:flex-row sm:items-center sm:justify-between">
          <div className="inline-flex rounded-2xl border border-slate-300 bg-slate-100 p-1 shadow-sm">
            {(["all", "active", "inactive"] as StatusFilter[]).map((opt) => {
              const label =
                opt === "all" ? "All" : opt === "active" ? "Active" : "Expired";

              const isActive = statusFilter === opt;

              return (
                <button
                  key={opt}
                  type="button"
                  onClick={() => setStatusFilter(opt)}
                  className={`rounded-xl px-4 py-2 text-[12.5px] font-black transition-all ${
                    isActive
                      ? "bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-md shadow-emerald-500/20"
                      : "text-slate-500 hover:bg-slate-200 hover:text-slate-800"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>

          <div className="relative w-full sm:w-[380px]">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-slate-400" />

            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, account, phone, plan, router…"
              className="h-10 w-full rounded-2xl border border-slate-300 bg-slate-50 pl-10 pr-3 text-[13px] font-medium text-slate-700 placeholder:text-slate-400 transition-all focus:border-emerald-400 focus:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-emerald-500/10"
            />
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex flex-col items-center justify-center rounded-3xl border border-slate-200 bg-slate-50/95 py-16 shadow-lg shadow-slate-300/25">
            <div className="flex h-14 w-14 items-center justify-center rounded-3xl border border-emerald-200 bg-emerald-50 shadow-lg shadow-emerald-200/40">
              <Loader2 className="h-7 w-7 animate-spin text-emerald-500" />
            </div>

            <p className="mt-3 text-[13px] font-bold text-slate-500">
              Loading extended customers…
            </p>
          </div>
        ) : error ? (
          <div className="flex items-start gap-3 rounded-3xl border border-rose-200 bg-rose-50 px-4 py-3.5 text-[13px] font-bold text-rose-700 shadow-sm shadow-rose-200/40">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <p>{error}</p>
          </div>
        ) : !filteredRows.length ? (
          <div className="rounded-3xl border border-slate-200 bg-slate-50/95 px-4 py-16 text-center shadow-lg shadow-slate-300/25">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-3xl bg-slate-200">
              <Users className="h-8 w-8 text-slate-500" />
            </div>

            <h3 className="text-[15px] font-black text-slate-800">
              {search ? "No Matching Customers" : "No Extended Customers"}
            </h3>

            <p className="mx-auto mt-1 max-w-md text-[13px] font-medium text-slate-500">
              {search
                ? `No customers found matching "${search}". Try adjusting your search terms.`
                : "There are no customers with access extensions yet."}
            </p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
            <div className="flex items-center justify-between gap-3 border-b border-slate-200 bg-slate-100/80 px-5 py-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
                  <Clock className="h-5 w-5" />
                </div>

                <div>
                  <h2 className="text-[14px] font-black text-slate-900">
                    Access Extensions
                  </h2>

                  <p className="text-[11.5px] font-medium text-slate-500">
                    {filteredRows.length} customer
                    {filteredRows.length !== 1 ? "s" : ""} found
                  </p>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full text-left">
                <thead className="border-b border-slate-200 bg-slate-100">
                  <tr>
                    {[
                      "",
                      "Customer",
                      "Plan & Router",
                      "Access Expiry",
                      "Last Extension",
                      "Count",
                      "Actions",
                    ].map((h, i) => (
                      <th
                        key={i}
                        className={`px-5 py-3.5 text-[11px] font-black uppercase tracking-widest text-slate-500 ${
                          i === 5 ? "text-center" : ""
                        } ${i === 6 ? "text-right" : ""}`}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-200/70 bg-slate-50">
                  {filteredRows.map((row) => {
                    const isOpen = openRowId === row.secret_id;
                    const active = isExtensionActive(row.access_expires_at);
                    const initials = getInitials(row.username);
                    const palette = avatarColor(row.username);

                    return (
                      <React.Fragment key={String(row.secret_id)}>
                        <tr className="transition-colors hover:bg-emerald-50/60">
                          <td className="px-5 py-4">
                            <button
                              type="button"
                              onClick={() =>
                                setOpenRowId((prev) =>
                                  prev === row.secret_id ? null : row.secret_id,
                                )
                              }
                              className="inline-flex h-9 w-9 items-center justify-center rounded-2xl border border-slate-300 bg-slate-50 text-slate-500 shadow-sm transition-colors hover:bg-slate-100 hover:text-slate-800"
                              title={isOpen ? "Hide history" : "Show history"}
                            >
                              {isOpen ? (
                                <ChevronDown className="h-4 w-4" />
                              ) : (
                                <ChevronRight className="h-4 w-4" />
                              )}
                            </button>
                          </td>

                          <td className="px-5 py-4">
                            <div className="flex items-center gap-3">
                              <div
                                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-[12px] font-black ${palette}`}
                              >
                                {initials}
                              </div>

                              <div className="min-w-0">
                                <p className="truncate text-[13px] font-black text-slate-900">
                                  {row.username || "Unknown"}
                                </p>

                                <p className="mt-0.5 text-[11.5px] font-bold text-slate-500">
                                  Account #{row.account_number}
                                </p>

                                {row.phone_number && (
                                  <p className="mt-0.5 flex items-center gap-1 text-[11px] font-medium text-slate-400">
                                    <Phone className="h-3 w-3" />
                                    {row.phone_number}
                                  </p>
                                )}
                              </div>
                            </div>
                          </td>

                          <td className="px-5 py-4">
                            <p className="text-[13px] font-black text-slate-800">
                              {row.plan_name || "No plan"}
                            </p>

                            {row.router_name && (
                              <p className="mt-0.5 flex items-center gap-1 text-[11.5px] font-bold text-slate-500">
                                <Router className="h-3 w-3" />
                                {row.router_name}
                              </p>
                            )}

                            {row.last_extension_reason && (
                              <p
                                className="mt-1 max-w-xs truncate text-[11.5px] font-medium text-amber-700"
                                title={row.last_extension_reason}
                              >
                                {row.last_extension_reason}
                              </p>
                            )}
                          </td>

                          <td className="px-5 py-4">
                            <div className="flex items-center gap-2">
                              <span
                                className={`inline-flex h-2 w-2 rounded-full ${
                                  active ? "bg-emerald-500" : "bg-slate-400"
                                }`}
                              />

                              <span className="text-[13px] font-bold text-slate-800">
                                {formatDateTime(row.access_expires_at)}
                              </span>
                            </div>

                            <p className="mt-0.5 text-[11.5px] font-medium text-slate-500">
                              Service: {formatDate(row.service_expires_at)}
                            </p>

                            <span
                              className={`mt-2 inline-flex rounded-full border px-2.5 py-1 text-[10.5px] font-black ${
                                active
                                  ? "border-emerald-200 bg-emerald-100 text-emerald-800"
                                  : "border-slate-300 bg-slate-200 text-slate-600"
                              }`}
                            >
                              {active ? "Active extension" : "Expired"}
                            </span>
                          </td>

                          <td className="px-5 py-4">
                            <p className="text-[13px] font-bold text-slate-700">
                              {formatDateTime(
                                row.last_extension_at ||
                                  row.last_extension_new_access_expires_at,
                              )}
                            </p>
                          </td>

                          <td className="px-5 py-4 text-center">
                            <span className="inline-flex min-w-[3rem] items-center justify-center rounded-2xl border border-sky-200 bg-sky-100 px-3 py-1.5 text-[12px] font-black text-sky-800">
                              {row.extension_count}
                            </span>
                          </td>

                          <td className="px-5 py-4 text-right">
                            <button
                              type="button"
                              onClick={() =>
                                navigate(`/customers/${row.secret_id}`)
                              }
                              className="inline-flex items-center gap-2 rounded-2xl border border-slate-300 bg-slate-50 px-4 py-2 text-[12.5px] font-black text-slate-600 shadow-sm transition-colors hover:bg-slate-100 hover:text-slate-800"
                            >
                              <User className="h-3.5 w-3.5" />
                              View
                            </button>
                          </td>
                        </tr>

                        {isOpen && (
                          <tr>
                            <td
                              colSpan={7}
                              className="bg-slate-100/70 px-5 py-4"
                            >
                              {row.extensions && row.extensions.length ? (
                                <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50 shadow-sm shadow-slate-300/30">
                                  <div className="flex items-center justify-between gap-3 border-b border-slate-200 bg-slate-100 px-4 py-3">
                                    <div className="flex items-center gap-2">
                                      <div className="flex h-8 w-8 items-center justify-center rounded-2xl bg-indigo-100 text-indigo-700">
                                        <Calendar className="h-4 w-4" />
                                      </div>

                                      <p className="text-[13px] font-black text-slate-900">
                                        Extension History
                                      </p>
                                    </div>

                                    <p className="text-[11.5px] font-bold text-slate-500">
                                      {row.extensions.length} extension
                                      {row.extensions.length !== 1 ? "s" : ""}
                                    </p>
                                  </div>

                                  <div className="overflow-x-auto">
                                    <table className="min-w-full text-left">
                                      <thead className="border-b border-slate-200 bg-slate-100/80">
                                        <tr>
                                          {[
                                            "Date & Time",
                                            "Previous Expiry",
                                            "New Expiry",
                                            "Reason",
                                            "Extended By",
                                          ].map((h) => (
                                            <th
                                              key={h}
                                              className="px-4 py-3 text-[10.5px] font-black uppercase tracking-widest text-slate-500"
                                            >
                                              {h}
                                            </th>
                                          ))}
                                        </tr>
                                      </thead>

                                      <tbody className="divide-y divide-slate-200/70 bg-slate-50">
                                        {row.extensions.map((ext) => (
                                          <tr
                                            key={String(ext.id)}
                                            className="transition-colors hover:bg-emerald-50/50"
                                          >
                                            <td className="px-4 py-3 text-[12.5px] font-bold text-slate-700">
                                              {formatDateTime(ext.created_at)}
                                            </td>

                                            <td className="px-4 py-3 text-[12.5px] font-medium text-slate-500">
                                              {formatDateTime(
                                                ext.previous_access_expires_at,
                                              )}
                                            </td>

                                            <td className="px-4 py-3 text-[12.5px] font-black text-slate-800">
                                              {formatDateTime(
                                                ext.new_access_expires_at,
                                              )}
                                            </td>

                                            <td className="max-w-xs truncate px-4 py-3 text-[12.5px] font-medium text-slate-600">
                                              {ext.reason || "—"}
                                            </td>

                                            <td className="px-4 py-3 text-[12.5px] font-medium text-slate-600">
                                              {ext.created_by || "—"}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              ) : (
                                <div className="rounded-3xl border border-slate-200 bg-slate-50 px-4 py-8 text-center">
                                  <p className="text-[13px] font-bold text-slate-500">
                                    No extension history available
                                  </p>
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExtendedCustomersPage;
